import { formatShortDate, formatShortTime, getLocalizedString } from "common/components/localization";
import { CalendarListOrder } from "common/generator/array-screens/calendar-list-order";
import { CalendarListMode } from "common/generator/components/calendar-list-mode";
import { getNowTimeZoneAgnostic, isEmptyOrUndefinedish } from "common/Support";
import Flatten from "lodash/flatten";
import * as React from "react";
import CalendarSchedule, { CalendarEvent } from "webapp/ui/views/calendar-schedule/calendar-schedule";
import { List as ListIcon } from "common/react-plucked";
import EmptyWrapper from "../../views/empty-wrapper/empty-wrapper";
import { BigLabel, BigLabelContainer } from "../../views/labels/labels";
import { EventWrapper, HeaderName, HeaderWrapper, SwitcherButton, Wrapper } from "./calendar-list-style";
import AppIcon from "common/app-icon";
import classNames from "classnames";
import { createPortal } from "react-dom";
import { withScrollOverlayID } from "webapp/chrome/views/scroll-view/scroll-view-overlay";
import kebabCase from "lodash/kebabCase";

interface CalendarEventGroup<T> {
    readonly name: string;
    readonly important: boolean;
    readonly events: CalendarEvent<T>[];
}

export type CalendarEventList<T> = CalendarEventGroup<T>[];

interface SavedState {
    mode: CalendarListMode;
}

interface Props<T> {
    readonly title: string | undefined;
    readonly eventList: CalendarEventList<T>;
    readonly onTap?: (event: CalendarEvent<T>) => void;
    readonly emptyMessage?: string;
    readonly defaultMode: CalendarListMode;
    readonly order: CalendarListOrder;
    readonly saveState: (state: SavedState) => void;
    readonly savedState: SavedState | undefined;
    readonly allowSwitch: boolean;
    readonly showTime: boolean;
}

interface OuterProps<T> extends Props<T> {
    readonly scrollOverlayID: string;
}

interface EventState {
    readonly touched: boolean;
}

interface EventProps<T> {
    readonly event: CalendarEvent<T>;
    readonly onTap?: (event: CalendarEvent<T>) => void;
    readonly showTime: boolean;
}

class Event<T> extends React.Component<EventProps<T>, EventState> {
    public state: EventState = { touched: false };

    private onTouchStart = (_ev: React.TouchEvent<HTMLDivElement>) => {
        this.setState({ touched: true });
    };

    private onTouchEnd = (_ev: React.TouchEvent<HTMLDivElement>) => {
        this.setState({ touched: false });
    };

    private onEventClicked(event: CalendarEvent<T>): void {
        if (this.props.onTap === undefined) return;
        this.props.onTap(event);
    }

    public render(): React.ReactNode {
        const {
            showTime,
            event: { name, description, start, end },
        } = this.props;

        const isPast = getNowTimeZoneAgnostic().getTime() > end.getTime();

        const labelledby = kebabCase(`cal-event-name-${name}-${formatShortTime(start)}`);

        return (
            <EventWrapper
                data-test="app-cal-event"
                touched={this.state.touched}
                onTouchStart={this.onTouchStart}
                onTouchEnd={this.onTouchEnd}
                className={classNames(isPast && "past")}
                onClick={() => this.onEventClicked(this.props.event)}
                role={this.props.onTap === undefined ? undefined : "button"}
                aria-labelledby={labelledby}>
                <div className="app-cal-event-inner">
                    <div className="app-cal-event-text-area">
                        <div id={labelledby} className="app-cal-event-name">
                            {name}
                        </div>
                        <div className="app-cal-event-desc">{description}</div>
                    </div>
                    {showTime && (
                        <div className="app-cal-time-wrapper">
                            <div className="app-cal-event-time">{formatShortTime(start)}</div>
                            {start.getTime() !== end.getTime() ? (
                                <div className="app-cal-event-time">{formatShortTime(end)}</div>
                            ) : (
                                <div className="app-cal-event-time" />
                            )}
                        </div>
                    )}
                </div>
            </EventWrapper>
        );
    }
}

function CalendarListInner<T>(p: React.PropsWithChildren<OuterProps<T>>) {
    const {
        eventList,
        emptyMessage,
        title,
        onTap,
        order,
        defaultMode,
        saveState,
        savedState,
        scrollOverlayID,
        allowSwitch,
        showTime,
    } = p;

    function createEventUI(event: CalendarEvent<T>, key: string): React.ReactNode {
        return <Event key={key} event={event} onTap={onTap} showTime={showTime} />;
    }

    function createGroupUI(eventGroup: CalendarEventGroup<T>, key: string): React.ReactNode {
        const events = eventGroup.events.map((e, i) => createEventUI(e, key + "," + i.toString()));

        const header: React.ReactNode = (
            <HeaderWrapper data-test="app-cal-header" className={classNames(eventGroup.important && "important")}>
                <HeaderName>{eventGroup.name}</HeaderName>
            </HeaderWrapper>
        );
        return (
            <div key={key}>
                {header}
                {events}
            </div>
        );
    }

    const [mode, setMode] = React.useState(savedState?.mode ?? defaultMode);
    const [viewDate, setViewDate] = React.useState(() => {
        const now = getNowTimeZoneAgnostic();
        let targetDay = getNowTimeZoneAgnostic();
        for (const group of eventList) {
            if (group.events.length === 0) continue;

            const delta = group.events[0].start.getTime() - now.getTime();
            if (order === CalendarListOrder.OldestFirst) {
                // show closest day to today with events in the future
                if (delta < 0) {
                    continue;
                } else {
                    targetDay = group.events[0].start;
                    break;
                }
            } else {
                // show closest day to today with events in the past
                if (delta > 0) {
                    continue;
                } else {
                    targetDay = group.events[0].start;
                    break;
                }
            }
        }
        return targetDay;
    });

    const onFloatingButtonClick = React.useCallback(() => {
        const newVal = mode === CalendarListMode.List ? CalendarListMode.Calendar : CalendarListMode.List;
        setMode(newVal);
        saveState({
            mode: newVal,
        });
    }, [saveState, mode]);

    let buttons: React.ReactNode;
    const scrollParent = document.getElementById(scrollOverlayID);
    if (scrollParent !== null && allowSwitch) {
        buttons = createPortal(
            <SwitcherButton
                onClick={onFloatingButtonClick}
                title={`Show ${mode === CalendarListMode.List ? "calendar" : "list"}`}>
                {mode === CalendarListMode.List ? (
                    <AppIcon icon="01-21-calendar-3" size={22} />
                ) : (
                    <ListIcon size={22} />
                )}
            </SwitcherButton>,
            scrollParent
        );
    }

    // NO REACT HOOKS AFTER THIS POINT

    if (eventList.length === 0) {
        return <EmptyWrapper>{emptyMessage ?? getLocalizedString("noUpcomingEvents")}</EmptyWrapper>;
    }

    let content: React.ReactNode;

    if (mode === CalendarListMode.List || !allowSwitch) {
        content = <div aria-label="calendar events">{eventList.map((eg, i) => createGroupUI(eg, i.toString()))}</div>;
    } else {
        const scheduleEvents = Flatten(eventList.map(s => s.events));
        const header = formatShortDate(viewDate, false) ?? "";
        content = (
            <CalendarSchedule<T>
                header={header}
                viewDate={viewDate}
                selection={undefined}
                onEventClick={onTap}
                onDateClick={setViewDate}
                events={scheduleEvents}
            />
        );
    }

    const hasTitle = !isEmptyOrUndefinedish(title);
    return (
        <Wrapper className={hasTitle ? "header" : ""}>
            {hasTitle && (
                <BigLabelContainer>
                    <BigLabel>{title}</BigLabel>
                </BigLabelContainer>
            )}
            {content}
            {buttons}
        </Wrapper>
    );
}

const HighlightedList = withScrollOverlayID(CalendarListInner);

function List<T>(props: Props<T>) {
    const TypedList = HighlightedList as React.ComponentType<Props<T>>;
    return <TypedList {...props} />;
}

const CalendarList = List;
export default CalendarList;
